import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Settings, LogOut, Sun, Moon } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useTheme } from '@/context/ThemeProvider';
import {
  selectUserName,
  selectUserEmail,
  selectOldUserDetails,
  selectRootGroup,
  selectCreatedGroup,
} from '@/store/selectors';
import { Avatar } from '@/components/ui/Avatar';
import { NavDropdown } from './NavDropdown';
import { StorageBar } from './StorageBar';

interface NavUserProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  updateModalOpen: (key: string | undefined) => void;
}

export function NavUser({ open, onToggle, onClose, updateModalOpen }: NavUserProps) {
  const navigate = useNavigate();
  const { handleLogout } = useSession();
  const { theme, toggle: toggleTheme } = useTheme();

  const { givenName, familyName } = useSelector(selectUserName);
  const email = useSelector(selectUserEmail);
  const oldUserDetails = useSelector(selectOldUserDetails) as {
    image?: { S: string };
  } | null;
  const rootGroup = useSelector(selectRootGroup) as {
    id: string;
    role: string;
    status: string;
    title: string;
  } | null;
  const createdGroup = useSelector(selectCreatedGroup) as {
    status: string;
  } | null;

  const fullName = [givenName, familyName].filter(Boolean).join(' ');
  const userImage = oldUserDetails?.image?.S;
  const isDisabled = createdGroup?.status === '3' || createdGroup?.status === '4';

  const showStorage =
    rootGroup?.role === 'admin' && (rootGroup?.status === '2' || rootGroup?.status === '1');

  const handleNavigate = (url: string) => {
    navigate(url);
    onClose();
  };

  const handleSignOut = () => {
    handleLogout();
    onClose();
  };
  return (
    <NavDropdown
      open={open}
      onClose={onClose}
      onTriggerClick={onToggle}
      align="right"
      className="w-64"
      trigger={
        <>
          <Avatar
            src={userImage}
            name={fullName || 'User'}
            size="sm"
            className="h-7 w-7 text-[10px]"
          />
          <span className="hidden max-w-[80px] truncate text-sm font-medium text-accent-foreground md:inline">
            {givenName}
          </span>
        </>
      }
    >
      {/* User info card */}
      <div className="border-b border-border-light px-3 py-3">
        <div className="flex items-center gap-3">
          <Avatar src={userImage} name={fullName || 'User'} size="md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{fullName}</p>
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          </div>
        </div>
      </div>

      {/* Storage section */}
      {showStorage && rootGroup && (
        <div className="border-b border-border-light px-3 py-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">{rootGroup.title}</p>
          <StorageBar rootGroupId={rootGroup.id} updateModalOpen={updateModalOpen} />
        </div>
      )}

      {/* Menu items */}
      <div className="py-1">
        <button
          type="button"
          disabled={isDisabled}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-accent-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => handleNavigate('/in/account/profile')}
        >
          <Settings className="h-4 w-4 text-muted-foreground" />
          Account Settings
        </button>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-accent-foreground transition-colors hover:bg-secondary"
          onClick={toggleTheme}
        >
          {theme === 'light' ? (
            <Moon className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Sun className="h-4 w-4 text-muted-foreground" />
          )}
          {theme === 'light' ? 'Dark mode' : 'Light mode'}
        </button>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive-light"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </NavDropdown>
  );
}
